import { Check, Copy } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface CopyButtonProps {
	/** Text written to the clipboard. */
	value: string;
	/** Accessible label, e.g. "Kopírovat IČO". */
	label: string;
}

/**
 * One-click copy-to-clipboard icon button with a transient "Zkopírováno"
 * confirmation. Clipboard access happens only in the click handler, so the
 * component is SSR-safe.
 */
export function CopyButton({ value, label }: CopyButtonProps) {
	const [copied, setCopied] = useState(false);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
		};
	}, []);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(value);
			setCopied(true);
			if (timeoutRef.current) clearTimeout(timeoutRef.current);
			timeoutRef.current = setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Hodnotu se nepodařilo zkopírovat.");
		}
	};

	return (
		<button
			type="button"
			onClick={handleCopy}
			aria-label={copied ? "Zkopírováno" : label}
			title={copied ? "Zkopírováno" : label}
			className="inline-flex items-center gap-1 rounded-sm p-0.5 text-muted-foreground hover:text-foreground transition-colors align-middle"
		>
			{copied ? (
				<>
					<Check className="h-3.5 w-3.5 text-green-600" />
					<span className="text-xs text-green-600">Zkopírováno</span>
				</>
			) : (
				<Copy className="h-3.5 w-3.5" />
			)}
		</button>
	);
}
